import {
    EXPENSE_SUB_MODE_LABELS,
    REVENUE_SUB_MODE_LABELS,
} from "@common/helpers/domains/accounting/scheduleApplicationFilters";
import {
    ExpenseSubMode,
    RevenueSubMode,
} from "@common/types/domains/accounting/schedule";
import {
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { ScheduleApplicationsFilters } from "./applications/scheduleApplicationRowHelpers";

/** The schedules list filter on a hub's sub-modes (deferred / accrued revenue, prepaid / accrued expenses). */
export interface ScheduleSubModeFilter {
    tabLabel: string;
    pluralNoun: string;
    labels: Record<string, string>;
    options: FilterMultiselectOption[];
    getValues: (filters: ScheduleApplicationsFilters) => string[];
    setValues: (
        filters: ScheduleApplicationsFilters,
        values: FilterMultiselectValue[],
    ) => ScheduleApplicationsFilters;
}

function isOneOf<T extends string>(members: readonly T[]) {
    return (value: FilterMultiselectValue): value is T =>
        typeof value === "string" && members.some((member) => member === value);
}

function toOptions(labels: Record<string, string>): FilterMultiselectOption[] {
    return Object.entries(labels).map(([value, label]) => ({ value, label }));
}

const isRevenueSubMode = isOneOf(Object.values(RevenueSubMode));
const isExpenseSubMode = isOneOf(Object.values(ExpenseSubMode));

export const REVENUE_SUB_MODE_FILTER: ScheduleSubModeFilter = {
    tabLabel: "Revenue",
    pluralNoun: "Revenue types",
    labels: REVENUE_SUB_MODE_LABELS,
    options: toOptions(REVENUE_SUB_MODE_LABELS),
    getValues: (filters) => filters.revenueSubModes,
    setValues: (filters, values) => ({
        ...filters,
        revenueSubModes: values.filter(isRevenueSubMode),
    }),
};

export const EXPENSE_SUB_MODE_FILTER: ScheduleSubModeFilter = {
    tabLabel: "Expense",
    pluralNoun: "Expense types",
    labels: EXPENSE_SUB_MODE_LABELS,
    options: toOptions(EXPENSE_SUB_MODE_LABELS),
    getValues: (filters) => filters.expenseSubModes,
    setValues: (filters, values) => ({
        ...filters,
        expenseSubModes: values.filter(isExpenseSubMode),
    }),
};

export function getSubModeFilterLabel(options: {
    filter: ScheduleSubModeFilter;
    value: string;
}): string {
    return options.filter.labels[options.value] ?? options.value;
}
